/**
 * Servicio para gestionar el envío de logs al backend
 */
import api from './api';

/**
 * Envía un lote de logs al backend para su almacenamiento
 * @param {Array} logs - Array de objetos de log a enviar
 * @returns {Promise} - Promesa que se resuelve cuando los logs se han enviado correctamente
 */
export const sendLogsToServer = async (logs) => {
  try {
    const response = await api.post('/logs', { logs });
    return response.data;
  } catch (error) {
    // Solo usar console.error para evitar generar más logs que causen loops
    console.error('Error enviando logs al servidor:', error);
    throw error;
  }
};

/**
 * Obtiene logs del servidor según los criterios de filtrado
 * @param {Object} filters - Filtros para los logs
 * @param {string} filters.level - Nivel mínimo de logs (debug, info, warn, error)
 * @param {string} filters.startDate - Fecha de inicio (formato ISO)
 * @param {string} filters.endDate - Fecha de fin (formato ISO)
 * @param {string} filters.search - Texto a buscar en los logs
 * @param {number} filters.limit - Número máximo de logs a devolver
 * @param {number} filters.page - Página de resultados (para paginación)
 * @returns {Promise} - Promesa que se resuelve con los logs obtenidos
 */
export const getLogsFromServer = async (filters = {}) => {
  try {
    const response = await api.get('/logs', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo logs del servidor:', error);
    throw error;
  }
};

/**
 * Descarga un archivo de logs según los criterios de filtrado
 * @param {Object} filters - Filtros para los logs (igual que getLogsFromServer)
 * @returns {Promise} - Promesa que se resuelve con la URL del archivo para descargar
 */
export const downloadLogsFile = async (filters = {}) => {
  try {
    // Usar fetch directamente para obtener un blob
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    
    const response = await fetch(`${api.defaults.baseURL}/logs/download?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('prokids_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error descargando archivo de logs:', error);
    throw error;
  }
};

export default {
  sendLogsToServer,
  getLogsFromServer,
  downloadLogsFile,
};
